// Catches a rendering crash so the shop sees a calm message and a reload
// button instead of a blank white screen mid-shift.
//
// React only supports error boundaries as class components, which is why this
// one file is not a function component.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Goes to the browser console for a developer. The user never sees it.
    console.error('A screen failed to render:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    // Deliberately plain English and Arabic without the translation hook,
    // because the failure may well be inside the translation provider itself.
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page px-4">
        <div className="card w-full max-w-md p-7 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-burgundy-red/10 text-burgundy-red">
            <AlertTriangle className="h-7 w-7" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-ink">The screen could not be displayed</h1>
          <p className="mt-1 text-sm text-ink-muted" dir="rtl">
            تعذّر عرض الشاشة
          </p>
          <p className="mt-3 text-sm text-ink-muted">
            Something unexpected happened. Reloading the page usually fixes it. Nothing was saved.
          </p>
          <button type="button" onClick={() => window.location.reload()} className="btn-primary mt-6 w-full">
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
